import { bnPageHead } from "@/lib/bn-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import {
  CityMatrix,
  ServiceFirstIndex,
  type IndexCity,
  type IndexService,
} from "@/components/city-index/CityIndexSections";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BN_LOCATIONS, BN_SERVICES, BN_SERVICE_SLUGS } from "@/content/bn/locations";
import { faqSchema } from "@/lib/schema";
import { HUB_FAQS_BN } from "@/content/hub-faqs";
import { SITE } from "@/content/site";
import { FaqCardGrid } from "@/components/FaqCardGrid";

const BN_PATH = "/bn/seo-services";
const EN_PATH = "/seo-services";
const TOTAL_PAGES = BN_LOCATIONS.length * BN_SERVICE_SLUGS.length;

const INDEX_SERVICES: IndexService[] = BN_SERVICE_SLUGS.map((s) => ({
  slug: s,
  title: BN_SERVICES[s].title,
  tagline: BN_SERVICES[s].tagline,
  search: [BN_SERVICES[s].title, s],
}));

const INDEX_CITIES: IndexCity[] = BN_LOCATIONS.map((l) => ({
  slug: l.slug,
  name: l.cityBn,
  region: l.regionBn,
  district: l.districtBn,
  industries: l.industries,
  search: [l.cityBn, l.city, l.regionBn, l.districtBn],
  paired: true,
}));

const TITLE = "এসইও সেবা ইনডেক্স — শহর অনুযায়ী সব পেজ";
const DESCRIPTION = `বাংলাদেশের ${BN_LOCATIONS.length}টি শহরের জন্য লোকাল এসইও, টেকনিক্যাল অডিট, ই-কমার্স এসইও, AEO/GEO ও কনটেন্ট কৌশল — মোট ${TOTAL_PAGES}টি ডেডিকেটেড পেজের সম্পূর্ণ ইনডেক্স।`;

export const Route = createFileRoute("/bn/seo-services/")({
  head: () =>
    bnPageHead({
      path: BN_PATH,
      enPath: EN_PATH,
      title: TITLE,
      desc: DESCRIPTION,
      keywords: ["এসইও সেবা বাংলাদেশ", "লোকাল এসইও শহর", "seo services bangladesh bangla"],
    }),
  component: BnServiceIndex,
});

function BnServiceIndex() {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "শহর ও সেবা অনুযায়ী এসইও পেজ — বাংলাদেশ",
    inLanguage: "bn-BD",
    numberOfItems: TOTAL_PAGES,
    itemListElement: BN_LOCATIONS.flatMap((l, ci) =>
      BN_SERVICE_SLUGS.map((s, si) => ({
        "@type": "ListItem",
        position: ci * BN_SERVICE_SLUGS.length + si + 1,
        url: `${SITE.url}${BN_PATH}/${l.slug}/${s}`,
        name: `${l.cityBn}-এ ${BN_SERVICES[s].title}`,
      })),
    ),
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "হোম", path: "/bn" },
          { name: "সেবাসমূহ", path: "/bn/services" },
          { name: "এসইও সেবা ইনডেক্স", path: BN_PATH },
        ]}
      />
      <JsonLd data={itemList} />
      <JsonLd data={{ ...faqSchema(HUB_FAQS_BN), inLanguage: "bn-BD" }} />

      <h1 className="sr-only">
        বাংলাদেশের সব শহর ও সেবার এসইও পেজ ইনডেক্স
      </h1>

      <Section
        eyebrow="সম্পূর্ণ ইনডেক্স"
        title={
          <>
            প্রতিটি শহর, প্রতিটি সেবা —{" "}
            <span className="text-gradient">এক ইনডেক্সে।</span>
          </>
        }
        subtitle={`${BN_LOCATIONS.length}টি শহর ও ${BN_SERVICE_SLUGS.length}টি বিশেষায়িত সেবা মিলিয়ে ${TOTAL_PAGES}টি আলাদা পেজ। আপনার বাজার ও প্রয়োজন অনুযায়ী সঠিক পেজটি এখানেই পাবেন।`}
      >
        <div className="grid lg:grid-cols-3 gap-8 -mt-4">
          <div className="lg:col-span-2 space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>
              প্রতিটি পেজ ওই শহরের বাস্তব তথ্য ধরে লেখা — স্থানীয় প্রতিযোগী,
              এলাকা, যে ইন্ডাস্ট্রিগুলো সত্যিই সার্চ করে এবং সেখানে আমি কী কী
              ডেলিভার করি। শুধু শহরের নাম বদলে দেওয়া টেমপ্লেট নয়।
            </p>
            <p>
              শহর জানা থাকলে শহর দিয়ে শুরু করুন, আর সেবা তুলনা করতে চাইলে সেবা
              দিয়ে — দুই পথেই একই জায়গায় পৌঁছাবেন।
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-sm text-muted-foreground">ইনডেক্সের আকার</div>
            <div className="font-display text-4xl mt-1">{TOTAL_PAGES}</div>
            <div className="text-sm text-muted-foreground mt-1">
              শহর × সেবা পেজ
            </div>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <Link to="/bn/services" className="text-primary hover:text-foreground">
                সব সেবা দেখুন →
              </Link>
              <Link to="/bn/case-studies" className="text-primary hover:text-foreground">
                কেস স্টাডি →
              </Link>
              <Link to={EN_PATH} hrefLang="en" className="text-primary hover:text-foreground">
                English index →
              </Link>
            </div>
          </div>
        </div>

        <ServiceFirstIndex lang="bn" services={INDEX_SERVICES} cities={INDEX_CITIES} />
      </Section>

      {/* শহর অনুযায়ী */}
      <Section
        eyebrow="শহর অনুযায়ী"
        title={<>সম্পূর্ণ শহর × সেবা তালিকা</>}
        className="pt-0"
      >
        <CityMatrix
          lang="bn"
          cities={INDEX_CITIES}
          services={INDEX_SERVICES}
          sortRegions={false}
          hubLabel={(l) => <>{l.name}-এ এসইও</>}
          serviceLabel={(l, s) => (
            <>
              {l.name}-এ {s.title}
            </>
          )}
        />
      </Section>
      <Section
        eyebrow="প্রশ্নোত্তর"
        title={<>পেজ বেছে নেওয়ার আগে <span className="text-gradient">সাধারণ প্রশ্ন।</span></>}
        className="pt-0"
      >
        <FaqCardGrid items={HUB_FAQS_BN} />
      </Section>
    </>
  );
}
